import { type KeyboardEvent, useCallback, useMemo, useState } from 'react';

import { useQuery } from '@apollo/client';
import { ArrowLeft, Check, Users } from 'lucide-react';
import Path from 'path-to-regexp';
import { useHistory, useParams } from 'react-router-dom';
import { StringParam, useQueryParam } from 'use-query-params';

import { Button } from '@/shadcn/button';
import { cn } from '@/shadcn/lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/shadcn/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/shadcn/tooltip';

import { gql } from '@/apollo/gql';
import { FastWorkflowOverviewHeader } from '@/components/fastWorkflowOverview/fastWorkflowOverviewHeader';
import { KanbanMain } from '@/components/layouts/kanban';
import { notify } from '@/components/toast';
import { ROUTE_WORKFLOW_OVERVIEW } from '@/constant/routes';
import { WORKFLOW_BATCH_JOB_SUBMITTERS_QUERY } from '@/gql/queries/batch-action.gql';
import { isTerminalBatchJobStatus, useBatchJob } from '@/hooks/useBatchJob';
import { useCreateBatchAction } from '@/hooks/useCreateBatchAction';
import { useRecentBatchItems } from '@/hooks/useRecentBatchItems';
import { useRetryBatchItem } from '@/hooks/useRetryBatchItem';
import { useUser } from '@/hooks/useUser';
import type {
  BatchJobFilter,
  BatchModeFilter
} from '@/hooks/useWorkflowBatchJobs';
import { useWorkflowBatchJobs } from '@/hooks/useWorkflowBatchJobs';
import { BatchJobDetailPane } from './batchJobDetailPane';
import { BatchJobList } from './batchJobList';
import { emptyStateMessage } from './batchJobPresentation';
import { BatchStatusGlyph } from './batchStatusGlyph';
import { BatchSubmitter, submitterLabel } from './batchSubmitter';
import { isBatchModeFilter, MODE_FILTERS } from './modeFilters';
import { isBatchJobFilter, STATUS_FILTERS } from './statusFilters';
import { actionIconsOf } from './workflowActionIcons';

export const WORKFLOW_HEADER_QUERY = gql(`
  query workflowHeaderQuery($workflowId: BigInt!) {
    workflowByRowId(rowId: $workflowId) {
      rowId
      name
      canUpdate
      isReader
      config {
        rowId
        overview
        initState
        initActions
        stateSchema
        actions
        transitions
        archiveCondition
      }
      groups {
        nodes {
          rowId
          label
          name
        }
      }
    }
  }
`);

const ALL_USERS = '__all__';

export function BatchActionHistoryPage() {
  const workflowId = useParams<{ id: string }>().id;
  const history = useHistory();
  const [selectedJobId, setSelectedJobId] = useQueryParam('job', StringParam);
  const [filter, setFilter] = useState<BatchJobFilter>('all');
  const [mode, setMode] = useState<BatchModeFilter>('all');
  // Sentinel rather than '' because a Radix SelectItem cannot hold an empty value.
  const [runBy, setRunBy] = useState<string>(ALL_USERS);

  const { user, isPrivileged } = useUser();
  const runByEmail = runBy === ALL_USERS ? undefined : runBy;
  // Privileged users browse everyone's history by default and narrow with the
  // "run by" dropdown; the server ignores this for anyone else.
  const allUsers = isPrivileged;

  // Not privilege-gated: a normal user gets exactly themselves back, which is
  // what turns their own cards from an address into a name.
  const { data: submittersData } = useQuery(
    WORKFLOW_BATCH_JOB_SUBMITTERS_QUERY,
    { variables: { workflowId } }
  );
  const submitters =
    submittersData?.workflowBatchJobSubmitters?.submitters ?? [];
  // email -> identity, so cards and the pane can render a person.
  const submittersByEmail = useMemo(
    () => Object.fromEntries(submitters.map(s => [s.email, s])),
    [submitters]
  );

  const { data: workflowData, loading: workflowLoading } = useQuery(
    WORKFLOW_HEADER_QUERY,
    {
      variables: { workflowId }
    }
  );
  const workflow = workflowData?.workflowByRowId;
  // Jobs store the group's LABEL (its config key, e.g. "conduentAdmin"); the
  // workflow's groups carry the name people actually recognize.
  const actionIcons = useMemo(
    () => actionIconsOf(workflow?.config?.actions),
    [workflow]
  );
  const groupNames = useMemo(
    () =>
      Object.fromEntries(
        (workflow?.groups?.nodes ?? [])
          .filter(g => !!g?.label)
          .map(g => [g?.label, g?.name])
      ),
    [workflow]
  );

  const { jobs, loading, hasMore, loadMore, refresh } = useWorkflowBatchJobs(
    workflowId,
    { filter, mode, allUsers, runBy: runByEmail }
  );

  const openJobId = selectedJobId ?? null;
  const { job, loading: jobLoading } = useBatchJob(openJobId);
  const feed = useRecentBatchItems(openJobId, {
    paused: !openJobId || isTerminalBatchJobStatus(job?.status)
  });
  const refreshFeed = feed.refresh;

  const [retryingItems, setRetryingItems] = useState<ReadonlySet<string>>(
    new Set()
  );
  // The retry settles in a second and the row it flips can be off screen, so the
  // outcome is announced rather than left to be spotted.
  const onSettled = useCallback((traceId: string, status: string) => {
    setRetryingItems(prev => {
      const next = new Set(prev);
      next.delete(traceId);
      return next;
    });
    if (status === 'DONE') {
      notify.success('The trace was retried successfully');
      return;
    }
    notify.error('The retry failed again — open the error for details');
  }, []);
  const retryBatchItem = useRetryBatchItem({ onSettled });
  const onRetryItem = useCallback(
    (traceId: string) => {
      if (!openJobId) {
        return;
      }
      setRetryingItems(prev => new Set(prev).add(traceId));
      // The job is terminal, so the feed is not polling: without this the row
      // keeps showing the failure it no longer has.
      retryBatchItem(openJobId, traceId).then(() => refreshFeed());
    },
    [openJobId, retryBatchItem, refreshFeed]
  );

  // Controlled so a digit shortcut can both pick a status and close the list,
  // the way Linear's picker behaves.
  const [statusOpen, setStatusOpen] = useState(false);
  const onStatusKeyDown = useCallback((event: KeyboardEvent) => {
    const picked = STATUS_FILTERS[Number(event.key) - 1];
    if (!picked) {
      return;
    }
    event.preventDefault();
    setFilter(picked.key);
    setStatusOpen(false);
  }, []);

  const goToOverview = useCallback(
    () =>
      history.push(Path.compile(ROUTE_WORKFLOW_OVERVIEW)({ id: workflowId })),
    [history, workflowId]
  );

  // The filter's empty state should name the person, not their address.
  const runByLabel = runByEmail
    ? submitterLabel(submittersByEmail[runByEmail] ?? { email: runByEmail })
    : undefined;

  const onSelect = useCallback(
    // 'replaceIn' belongs on the setter, not on useQueryParam: selecting a card
    // must not push a history entry, or Back walks every card the user clicked.
    (jobId: string) => setSelectedJobId(jobId, 'replaceIn'),
    [setSelectedJobId]
  );

  // Retry writes a link signed as the job's original submitter, so it is offered
  // only on your own jobs — independent of which filter is showing them.
  const canRetry = !!job?.runBy && job.runBy === user?.email;

  const [retryingAll, setRetryingAll] = useState(false);
  const createBatchAction = useCreateBatchAction({
    onSuccess: jobId => {
      // Surface the new job and open it, so the retry has somewhere to be seen.
      refresh();
      setSelectedJobId(jobId, 'replaceIn');
    }
  });

  // "Retry all failed" submits a NEW batch over the failed subset — a different
  // mechanism from the per-row retry, which re-runs one item inside the same job.
  const onRetryFailed = useCallback(
    async (failedTraceIds: string[]) => {
      if (!job || failedTraceIds.length === 0) {
        return;
      }
      setRetryingAll(true);
      try {
        await createBatchAction({
          action: {
            workflowId,
            actionKey: job.actionKey,
            groupLabel: job.groupLabel,
            formData: job.formData,
            actionTitle: job.actionTitle ?? undefined
          },
          selection: { mode: 'explicit', ids: failedTraceIds }
        });
      } finally {
        setRetryingAll(false);
      }
    },
    [job, workflowId, createBatchAction]
  );

  return (
    <>
      <FastWorkflowOverviewHeader
        activeView="batchActions"
        isLoading={workflowLoading}
        workflow={workflow}
      />
      <KanbanMain loading={workflowLoading}>
        {/* w-full: KanbanMain is a flex ROW, so without it this pane sizes to
            its content and the view occupies only part of the viewport. */}
        <div className="flex h-full min-h-0 w-full flex-col">
          <div className="flex items-center gap-2 border-border border-b bg-card px-3 py-2">
            {/* Arrow alone: it sits in the batch view's own toolbar, so there is
                only one place it can go back to. The label lives in the tooltip
                and in the accessible name. */}
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    aria-label="Workflow overview"
                    className="size-6 shrink-0 cursor-pointer [&_svg]:size-3.5"
                    data-testid="batch-back-to-overview"
                    onClick={goToOverview}
                    size="icon"
                    variant="ghost"
                  >
                    <ArrowLeft />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">Workflow overview</TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <Select
              onOpenChange={setStatusOpen}
              onValueChange={value => {
                if (isBatchJobFilter(value)) {
                  setFilter(value);
                }
              }}
              open={statusOpen}
              value={filter}
            >
              <SelectTrigger
                className="h-7 w-[180px] cursor-pointer py-0 text-xs"
                data-testid="batch-status-filter"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent onKeyDown={onStatusKeyDown}>
                {STATUS_FILTERS.map((f, index) => (
                  <SelectItem
                    className="pr-2 text-xs"
                    hideIndicator
                    key={f.key}
                    trailing={
                      <span className="ml-auto flex shrink-0 items-center gap-1.5 pl-3 text-[10px] text-foreground/40">
                        {filter === f.key ? (
                          <Check className="size-3 text-foreground/70" />
                        ) : null}
                        {index + 1}
                      </span>
                    }
                    value={f.key}
                  >
                    <span className="flex items-center gap-2 leading-none">
                      {/* -translate-y-px: centring the boxes leaves a 14px mark
                          sitting low against 12px text, whose box carries more
                          space below the letters than above them. */}
                      <BatchStatusGlyph
                        className={cn('-translate-y-px', f.tone)}
                        data-testid={`batch-status-filter-icon-${f.key}`}
                        kind={f.glyph}
                      />
                      {f.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              onValueChange={value => {
                if (isBatchModeFilter(value)) {
                  setMode(value);
                }
              }}
              value={mode}
            >
              <SelectTrigger
                className="h-7 w-[180px] cursor-pointer py-0 text-xs"
                data-testid="batch-mode-filter"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MODE_FILTERS.map(f => (
                  <SelectItem className="text-xs" key={f.key} value={f.key}>
                    <span className="flex items-center gap-2 leading-none">
                      <f.icon
                        aria-hidden
                        className="-translate-y-px size-3.5 shrink-0"
                        data-testid={`batch-mode-filter-icon-${f.key}`}
                      />
                      {f.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {isPrivileged && (
              <Select onValueChange={value => setRunBy(value)} value={runBy}>
                <SelectTrigger
                  className="h-7 w-[220px] cursor-pointer py-0 text-xs"
                  data-testid="batch-runby-filter"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem className="text-xs" value={ALL_USERS}>
                    <span className="flex items-center gap-2 leading-none">
                      <Users
                        aria-hidden
                        className="-translate-y-px size-3.5 shrink-0"
                      />
                      All users
                    </span>
                  </SelectItem>
                  {submitters.map(identity => (
                    <SelectItem
                      className="text-xs"
                      key={identity.email}
                      value={identity.email}
                    >
                      <BatchSubmitter identity={identity} />
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            <span className="flex-1" />
          </div>

          <div className="flex min-h-0 flex-1">
            {/* Even split: the cards are one line of content, so a wider list
                just adds whitespace — the space is better spent on the feed. */}
            <div className="min-w-0 flex-1 border-border border-r bg-card">
              <BatchJobList
                actionIcons={actionIcons}
                emptyMessage={emptyStateMessage(filter, runByLabel)}
                groupNames={groupNames}
                hasMore={hasMore}
                jobs={jobs}
                loading={loading}
                onLoadMore={loadMore}
                onSelect={onSelect}
                selectedJobId={openJobId}
                submitters={submittersByEmail}
              />
            </div>
            <div className="min-w-0 flex-1 bg-card">
              <BatchJobDetailPane
                actionIcon={job ? actionIcons[job.actionKey] : undefined}
                feed={{
                  items: feed.items,
                  latestArrivalIds: feed.latestArrivalIds,
                  onLoadOlder: feed.loadOlder
                }}
                feedLoading={feed.loading}
                groupNames={groupNames}
                job={job}
                loading={jobLoading}
                onRetryFailed={canRetry ? onRetryFailed : undefined}
                onRetryItem={canRetry ? onRetryItem : undefined}
                retrying={retryingAll}
                retryingItems={retryingItems}
                submitter={
                  job?.runBy ? submittersByEmail[job.runBy] : undefined
                }
              />
            </div>
          </div>
        </div>
      </KanbanMain>
    </>
  );
}
